import { Button, Card, NotificationMessage, Typography } from 'ui/atoms';
import { useStyles } from './ProducerCheckout.styles';
import getSymbolFromCurrency from 'currency-symbol-map';

import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { CheckoutProps } from './ProducerCheckout.types';
import { useLocale } from 'hooks/useLocale/useLocale';
import { ReactComponent as LogoIcon } from 'assets/stage-clip-logo-black-text.svg';
import { KeyboardBackspace } from '@mui/icons-material';
import { useState } from 'react';
import { PaymentIntentResult } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import { useUpdatePaymentDetails, updateProducerPaymentDetails } from 'api/producer-checkout/producer-checkout';
import { useClient } from 'hooks/useClient/useClient';
import { useActivityTrack } from 'hooks/useActivityTrack/useActivityTrack';
import { ActivityTypes, CheckoutActivityCode } from 'api/activityTrack/activityTrack.types';
import { CircularProgress } from '@mui/material';
import { StripeInfoCard } from 'app/producerCheckout/stripeInfoCard/StripeInfoCard';

export const ProducerCheckout = ({ paymentInfo, paymentId, eventId, setPageLoading, refetch }: CheckoutProps) => {
  const client = useClient();
  const styles = useStyles();
  const { onActivityTrack } = useActivityTrack();

  const [nameCheck, setNameCheck] = useState(paymentInfo.userName);
  const [emailCheck, setEmailCheck] = useState(paymentInfo.email);
  const { formatMessage } = useLocale();
  const elements = useElements();
  const stripe = useStripe();

  const [isLoading, setLoading] = useState(false);

  const termsUrl = process.env.REACT_APP_TERMS_PAGE_URL;
  const privacyUrl = process.env.REACT_APP_PRIVACY_PAGE_URL;

  const [handleUpdatePaymentDetails, { isLoading: isPaymentUpdateLoading }] = useUpdatePaymentDetails({
    onError: async (data) => {
      refetch();
      window.onbeforeunload = null;
    },
    onSuccess: async (data) => {
      refetch();
      window.location.href = `${process.env.REACT_APP_PRODUCER_URL}/${
        data?.eventType ? `name-reading/${eventId}` : 'producer'
      }`;
    },
  });

  const handleSubmit = async (event: any) => {
    console.log('handleSubmit called', event);
    toast.dismiss();
    window.onbeforeunload = () => formatMessage({ id: 'global.actions.dont_close_text' });
    setLoading(true);
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    console.log('Calling stripe.confirmPayment');
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (result.error) {
      onErrorPayment(result.error);
      setLoading(false);
    } else {
      onSuccessPayment(result.paymentIntent);
    }
    window.onbeforeunload = null;
  };

  const onErrorPayment = async (StripeError: PaymentIntentResult['error']) => {
    const description = formatMessage(
      { id: 'checkout.payment_page.stripe_error_description' },
      { errorMsg: StripeError?.message },
    );

    await onActivityTrack(
      ActivityTypes.Checkout,
      CheckoutActivityCode.CheckoutPaymentFailed,
      paymentInfo.institutionName || paymentInfo.institutionId,
      description,
      paymentInfo.email || '',
      paymentInfo.userName,
    );
    toast.error(<NotificationMessage type="error">{StripeError?.message}</NotificationMessage>, { autoClose: false });
  };

  // Define handler for Express Checkout payments (Apple Pay / Google Pay)
  const handleExpressCheckout = async (event: any) => {
    console.log('handleExpressCheckout called');
    setLoading(true);
    toast.dismiss();

    if (!stripe || !elements) {
      console.error('Stripe has not loaded yet');
      setLoading(false);
      return;
    }

    try {
      await updateProducerPaymentDetails({ client, paymentId, body: { ...paymentInfo, paymentId } });

      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        onErrorPayment(result.error);
      } else {
        onSuccessPayment(result.paymentIntent);
      }
    } catch (err: any) {
      toast.error(<NotificationMessage type="error">{err?.message}</NotificationMessage>);
    } finally {
      setLoading(false);
    }
  };

  const onSuccessPayment = async (result: PaymentIntentResult['paymentIntent']) => {
    console.log('onSuccessPayment called', result);
    await handleUpdatePaymentDetails({
      client,
      body: {
        // ...paymentInfo,
        stripeId: paymentInfo.stripeId,
        paymentId,
        transactionDetails: result ? JSON.stringify(result) : '',
        userId: paymentInfo.userId
      },
      paymentId,
    });
    setPageLoading(true);
  };

  const handleBack = () => {
    setPageLoading(true);
    window.location.href = `${process.env.REACT_APP_PRODUCER_URL}/${
      paymentInfo?.eventType ? `name-reading/${eventId}` : 'producer'
    }`;
    setPageLoading(false);
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Card className={styles.productDetails}>
        <div className={styles.backButtonContainer}>
          <Button
            variant="text"
            className={styles.backButton}
            disabled={isLoading || isPaymentUpdateLoading}
            onClick={() => handleBack()}
          >
            <KeyboardBackspace />
            {formatMessage({ id: 'global.actions.back' })}
          </Button>
        </div>
        <div className={styles.logoContainer}>
          {!paymentInfo.logoUrl ? (
            <LogoIcon />
          ) : (
            <div className={styles.instituteLogoContainer}>
              <div className={styles.combinedLogoContainer}>
                <div className={styles.instituteLogoContainer}>
                  <img
                    alt="Institution Logo"
                    src={paymentInfo.logoUrl || './StageClip_Logo_Default_4000px-1.png'}
                    className={styles.institutelogoTag}
                  />
                </div>
                {paymentInfo.logoUrl && <div className={styles.dividerLine}></div>}
                <div className={styles.stageClipLogoContainer}>
                  <img
                    alt="StageClip Logo"
                    src={'./StageClip_Logo_Default_4000px-1.png'}
                    className={styles.institutelogoTag}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.totalPayContainer}>
          {isLoading ? (
            <CircularProgress className={styles.totalPayContainer} />
          ) : (
            <div>
              <div className={styles.priceContainer}>
                <Typography variant="h6" className={styles.taxPayText}>
                  Name check credits
                </Typography>
                <Typography>
                  {`${getSymbolFromCurrency(paymentInfo.currency)}${paymentInfo.totalAmount.toFixed(2)}`}
                </Typography>
              </div>

              <div className={styles.priceContainer}>
                <Typography variant="h6" className={styles.totalPayHeader}>
                  {formatMessage({ id: 'checkout.payment_page.total_pay' })}
                </Typography>
                <Typography variant="h6" className={styles.totalPayText}>
                  {`${getSymbolFromCurrency(paymentInfo.currency)}${paymentInfo.totalAmount.toFixed(
                    2,
                  )} (${paymentInfo.currency.toUpperCase()})`}
                </Typography>
              </div>
            </div>
          )}
        </div>
        <div className={styles.productDetailsContainer}>
          <StripeInfoCard
            nameCheck={nameCheck}
            emailCheck={emailCheck}
            setEmailCheck={setEmailCheck}
            setNameCheck={setNameCheck}
          />
        </div>

        <div className={styles.termsConditionLargeContainer}>
          <Typography variant="body2" className={styles.poweredByTextContainer}>
            {formatMessage({ id: 'checkout.payment_page.powered_by' })}
            <Typography variant="body2" className={styles.stageClipText}>
              {formatMessage({ id: 'checkout.payment_page.stage_clip_text' })}
            </Typography>
          </Typography>
          <div className={styles.divider} />
          <a className={styles.poweredByTextContainer} target="_blank" href={termsUrl} rel="noreferrer">
            {formatMessage({ id: 'checkout.payment_page.terms_text' })}
          </a>
          <a className={styles.poweredByTextContainer} target="_blank" href={privacyUrl} rel="noreferrer">
            {formatMessage({ id: 'checkout.payment_page.privacy_text' })}
          </a>
        </div>
      </Card>
      <Card className={styles.paymentDetails}>
        <div className={styles.paymentCard}>
          <div className={styles.expressCheckoutElement}>
            <ExpressCheckoutElement
              id="express-checkout-element"
              onConfirm={(e) => {
                if (!stripe || isLoading || isPaymentUpdateLoading) return;
                handleExpressCheckout(e);
              }}
              options={{
                wallets: {
                  applePay: 'always',
                  googlePay: 'always',
                },
                buttonHeight: 44,
                layout: {
                  maxColumns: 1,
                },
              }}
            />

            {(!stripe || isLoading || isPaymentUpdateLoading) && <div className={styles.disabledOverlay}></div>}
          </div>

          <PaymentElement id="payment-element" className={styles.paymentElement} />
          {/* {paymentRequest && <PaymentRequestButtonElement options={{ paymentRequest }} />} */}
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              type="submit"
              className={styles.paymentButton}
              disabled={!stripe || isLoading || isPaymentUpdateLoading || emailCheck === '' || nameCheck === ''}
            >
              {formatMessage({ id: 'checkout.payment_page.pay_text' })}
            </Button>
          )}
        </div>
        <div className={styles.termsConditionMobileContainer}>
          <Typography variant="body2" className={styles.poweredByTextContainer}>
            {formatMessage({ id: 'checkout.payment_page.powered_by' })}
            <Typography variant="body2" className={styles.stageClipText}>
              {formatMessage({ id: 'checkout.payment_page.stage_clip_text' })}
            </Typography>
          </Typography>
          <div className={styles.divider} />
          <a className={styles.poweredByTextContainer} target="_blank" href={termsUrl} rel="noreferrer">
            {formatMessage({ id: 'checkout.payment_page.terms_text' })}
          </a>
          <a className={styles.poweredByTextContainer} target="_blank" href={privacyUrl} rel="noreferrer">
            {formatMessage({ id: 'checkout.payment_page.privacy_text' })}
          </a>
        </div>
      </Card>
    </form>
  );
};
